import {
  AttributionType,
  CampaignStatus,
  OnsiteCampaign,
  OnsiteCampaignType,
  CampaignFilterRequest,
} from './campaign.model';
import { LanguageService } from '../core/language.service';
import { SessionCondition, SessionConditionType } from '../audience/components/session-condition-group.model';
import { SubscriptionFrequency } from '../subscription/subscription.model';
import { Column } from './response-lists/response-lists.component';
import { AccountCustomFontFile, AccountCustomFontType } from '../account-settings/account-settings-models';
import { IntegrationType } from 'app/automations/integrations-models';
import { BrandVariable } from 'app/account/brand-variable';
import { MediaModel } from 'app/shared/media-library/media-library.model';
import { Currency } from '../shared/account/Currency';
import { Tier } from '../shared/account/tier.model';
import { DataSourceType } from '../data-sources/data-sources.models';

export interface FilterString {
  text: string;
  category: string;
}

export enum WidgetStatus {
  Enabled = 'Enabled',
  Disabled = 'Disabled',
  Archived = 'Archived',
}

export enum TypeText {
  Label = 'label',
  TextProperties = 'textProperties',
}

export interface WidgetSimpleInfo {
  id?: number;
  name?: string;
  order?: number;
  audienceId?: string;
  sessionConditionGroupId?: number;
  campaignId?: number;
  campaignName?: string;
  campaignType?: OnsiteCampaignType;
  campaignStatus?: CampaignStatus;
  previewImageUrl?: string;
  thumbnail?: string;
  lastUpdated?: string;
  hasInputFields?: boolean;
  // Dynamic naming
  dynamicName?: string;
}

export interface Widget {
  id?: number;
  name?: string;
  campaignId?: number;
  campaignName?: string;
  campaignType?: OnsiteCampaignType;
  siteId?: number;
  campaign?: OnsiteCampaign;
  status?: WidgetStatus;
  states?: Array<WidgetState>;
  layout: WidgetLayout;
  conditions?: SessionCondition[];
  sessionConditionGroupId?: number;
  previewBackgroundColor?: string;
  lastPublished?: number;
  createdTime?: number;
  updatedTime?: number;
  order?: number;
  audienceId?: string;
  responseListId?: string;
  isPoweredByDisabled?: boolean;
  excludeFromSeoPreview?: boolean;
  hasInputFields?: boolean;
  hasUnpublishedChanges?: boolean;
  conditionsAndAudienceOperation?: AndOrOperator;
  transition?: WidgetTransition;
  splitInputsToSteps?: boolean;
  slidingFormConfig?: WidgetSlidingFormConfig;
  ignoreAutomaticAnimation?: boolean;
  usePartialFormSubmit?: boolean;
  hasEmbeddedWidget?: boolean;
  horizontalAlignment?: ComponentAlignment;
  width?: string;
  zIndex?: number | null;
  formRegistrationType?: IntegrationType;
  eventScripts?: WidgetEventScriptsEntity;
  customCode?: CustomCode;
  attributionType?: AttributionType;
  brandVariables?: BrandVariable;
  formSettings?: FormSettings;
  dynamicName?: string;
  outcomes?: Outcome[];
}

export interface Outcome {
  name?: string;
  stateId: number | null;
  answers: OutcomeAnswer[];
  isDefault: boolean;
  customDataUpdate?: OutcomeCustomDataUpdate;
  // Computed properties for UI
  canDrag?: boolean;
  displayName?: string | number;
  editName?: boolean;
}

export interface OutcomeAnswer {
  question: string;
  operator: OutcomeOperator;
  andOrOperator?: AndOrOperator;
  value: string | string[];
  // Computed properties for UI
  availableValues?: string[];
}

export interface OutcomeCustomDataUpdate {
  key: string;
  value: string;
}

export enum AndOrOperator {
  And = 'And',
  Or = 'Or',
}

export enum OutcomeOperator {
  Equals = 'Equals',
  NotEquals = 'NotEquals',
  GreaterThan = 'GreaterThan',
  LessThan = 'LessThan',
  GreaterThanOrEqual = 'GreaterThanOrEqual',
  LessThanOrEqual = 'LessThanOrEqual',
}

export interface FormSettings {
  dataSourceId?: string;
  success: FormSettingEntityProps;
  conflictError: FormSettingEntityProps;
  unknownError: FormSettingEntityProps;
  validationError: FormSettingEntityProps;
  $type?: string;
}

export enum FormSettingsHandlerTypes {
  Success = 'success',
  ValidationError = 'validationError',
  ConflictError = 'conflictError',
  UnknownError = 'unknownError',
}

export interface FormSettingEntityProps {
  $type: string;
  message: string;
  stepId: number;
}

export interface WidgetSlidingFormConfig {
  progressStyle?: SlidingFormProgressStyle;
  nextButtonText?: string;
}

export interface WidgetEventScriptsEntity {
  widgetOpened?: string;
  widgetClosed?: string;
  beforeFormSubmitted?: string;
  afterFormSubmitted?: string;
  buttonClicked?: string;
  stateSwitched?: string;
  widgetMinimized?: string;
}

export interface CustomCode {
  css?: string;
  componentsCSS?: Record<string, string>;
}

export enum SlidingFormProgressStyle {
  None = 'None',
  Dots = 'Dots',
  ProgressBar = 'ProgressBar',
}

export interface ConfigSlideIn {
  selectedSlidingFormProgressStyle: SlidingFormProgressStyle;
  selectedSlidingFormLabelName: string;
}

export interface UpdateWidgetLayoutAndPositionRequest {
  layout: WidgetLayout;
  position: WidgetPosition;
}

export interface WidgetCondition {
  id?: number;
  type: SessionConditionType;
  value?: string;
  operator?: string;
  show: boolean;
}

export enum WidgetConditionLanguages {
  English = 'English',
  Swedish = 'Swedish',
  Norwegian = 'Norwegian',
  Finnish = 'Finnish',
  Danish = 'Danish',
  Arabic = 'Arabic',
  Bengali = 'Bengali',
  Chinese = 'Chinese',
  Dutch = 'Dutch',
  French = 'French',
  German = 'German',
  Hindustani = 'Hindustani',
  Italian = 'Italian',
  Malay = 'Malay',
  Polish = 'Polish',
  Portuguese = 'Portuguese',
  Russian = 'Russian',
  Spanish = 'Spanish',
}

export enum WidgetConditionType {
  Query = 'Query',
  QueryParam = 'QueryParam',
  Device = 'Device',
  Cookie = 'Cookie',
  UtmCampaign = 'UtmCampaign',
  UtmSource = 'UtmSource',
  UtmMedium = 'UtmMedium',
  BrowserLanguage = 'BrowserLanguage',
  FacebookVisit = 'FacebookVisit',
  InstagramVisit = 'InstagramVisit',
  OtherSocialMediaVisit = 'OtherSocialMediaVisit',
  MarketingEmailVisit = 'MarketingEmailVisit',
  CookieEquals = 'CookieEquals',
  CookieExists = 'CookieExists',
  JsExpression = 'JsExpression',
  OnlyMobile = 'OnlyMobile',
  OnlyDesktop = 'OnlyDesktop',
  Domain = 'Domain',
  NewVisitor = 'NewVisitor',
  ReturningVisitor = 'ReturningVisitor',
  CampaignOpened = 'CampaignOpened',
  CampaignDismissal = 'CampaignDismissal',
  CampaignConversion = 'CampaignConversion',
  CampaignClickthrough = 'CampaignClickthrough',
  CampaignSurveyResponse = 'CampaignSurveyResponse',
  IdentifiedEmailMarketing = 'IdentifiedEmailMarketing',
  LoggedIn = 'LoggedIn',
  Tablet = 'Tablet',
  MobileOrTablet = 'MobileOrTablet',
  OperatingSystem = 'OperatingSystem',
}

export interface WidgetTemplate {
  id: number;
  name: string;
  tags?: Array<string>;
  layout: WidgetLayout;
  status: CampaignStatus;
  numberOfUsages?: number;
  previewImages?: Array<WidgetPreviewImage>;
  previewBackgroundColor?: string;
}

// TODO: This entity will later be removed and properties are going to be moved one level up to template
export interface WidgetPreview {
  id?: number;
  previewImages?: Array<WidgetPreviewImage>;
  backgroundColor?: string;
}

export interface WidgetPreviewImage {
  id: number;
  url: string;
  width: number;
  height: number;
  orderNumber: number;
  type?: WidgetPreviewImageType;
  src?: string;
  thumb?: string;
}

export enum WidgetPreviewImageType {
  Layout = 'Layout',
  Design = 'Design',
}

export interface WidgetState {
  id?: number;
  /** The saved content's version; a save of an older copy is refused with 409. */
  version?: string;
  type: WidgetComponentType;
  width?: string;
  height?: string;
  containers?: Array<WidgetContainer>;
  floatingContainer?: WidgetContainer;
  position: WidgetPosition;
  layout?: WidgetLayout;
  background?: BackgroundProperties;
  radius?: ComponentRadius;
  shadow?: ComponentShadow;
  padding?: FourSideStylingEntity;
  margin?: FourSideStylingEntity;
  border?: ComponentBorder;
  widgetId?: number;
  order: number;
  automationId?: string;
  previewImageUrl?: string;
  actionType?: ButtonActionType;
  actionValue?: string;
  actionScript?: string;
  actionTriggerDelay?: number;
  /** Automation fired when the state itself is clicked. Distinct from the legacy form-submission automationId. */
  actionAutomationId?: string;
  mobile?: MobileProperties;
  direction?: Direction;
  alternativeStepApp?: string;
  alternativeStepListId?: string;
  alternativeStepId?: number;
  /** @deprecated The API mirrors alternativeStep onto these on read and falls back to them on write
   *  (`AlternativeStepApp ?? SubscriberCheckApp`), so a round-tripped value here resurrects a cleared step:
   *  null them whenever alternativeStep is written. */
  subscriberCheckApp?: string | null;
  /** @deprecated See subscriberCheckApp. */
  subscriberCheckListId?: string | null;
  /** @deprecated See subscriberCheckApp. */
  subscriberCheckStateId?: number | null;
  // Only for front-end
  name?: string;
  titleState?: string;
  wcagProperties?: WcagProperties;
  subType?: string;
  rotation?: number;
  productTourProperties?: ProductTourProperties;
  primaryCanvasStateId?: number;
  isPrimaryCanvasState?: boolean;
  isInnerCanvasState?: boolean;
}

export interface ProductTourProperties {
  targetSelector?: string;
  tourPosition?: TourPosition;
  tourAlignment?: TourAlignment;
  distance?: number;
  offset?: number;
  offsetX?: number;
  offsetY?: number;
  highlightColor?: string;
  urlMatch?: string;
  backDropFilter?: string;
  dismissible?: boolean;
  allowInteraction?: boolean;
  zIndex?: number;
  tourConnectorStyle?: TourConnectorStyle;
  tourConnectorSize?: TourConnectorSize;
  tourConnectorTargetSize?: TourConnectorSize;
  tourConnectorTargetDecoration?: TourConnectorTargetDecoration[];
  displayEvenIfTargetNotFound?: boolean;
  connectorPosition?: number; // Position along the edge (0-100), where 0 is left/top and 100 is right/bottom, default is 50 (center)
  connectorTargetPosition?: number; // Position along the target edge (0-100), where 0 is left/top and 100 is right/bottom, default is 50 (center)
}

export enum TourConnectorStyle {
  None = 'None',
  SpeechBubbleTriangle = 'SpeechBubbleTriangle', // Triangle attached to the box
  Arrow = 'Arrow', // SVG arrow between elements
  SolidLine = 'SolidLine', // Simple line
  CurvedLine = 'CurvedLine', // Bézier or smooth SVG line
}

export enum TourConnectorTargetDecoration {
  Ball = 'Ball', // Simple dot or circle
  PulsingBall = 'PulsingBall', // Animated attention effect
  HighlightBox = 'HighlightBox', // Outline around the component
}

export enum TourConnectorSize {
  Small = 'Small',
  Medium = 'Medium',
  Large = 'Large',
}

export enum TourPosition {
  Top = 'Top',
  Bottom = 'Bottom',
  Left = 'Left',
  Right = 'Right',
}

export enum TourAlignment {
  Start = 'Start', // 'Up' if Left/Right, 'Left' if Top/Bottom
  Center = 'Center',
  End = 'End', // 'Down' if Left/Right, 'Right' if Top/Bottom
}

export interface WidgetContainer {
  id: number;
  percentWidth: number;
  height?: string;
  components: Array<any>;
  background?: BackgroundProperties;
  shadow?: ComponentShadow;
  border?: ComponentBorder;
  radius?: ComponentRadius;
  padding?: FourSideStylingEntity;
  margin?: FourSideStylingEntity;
  alignment?: { vertical: ComponentAlignment };
  direction?: Direction;
  hideOnMobile?: boolean;
  hideOnDesktop?: boolean;
  order: number;
  mobile?: MobileProperties;
  visualEffect?: ContainerVisualEffect;
  // Just for front-end
  type: WidgetComponentType;
  subType: WidgetComponentSubType;
}

export interface ImageComponent extends WidgetComponent {
  fileName: string;
  url: string;
  width: string;
  height: string;
  background?: BackgroundProperties;
  border?: ComponentBorder;
  margin?: FourSideStylingEntity;
  // For floating components
}

export interface FloatingImageComponent extends ImageComponent {
  positionX: number;
  positionY: number;
}

export interface ConsentCheckboxComponent extends CheckboxComponent {
  customPolicyUrl?: string;
  hideCheckbox?: boolean;
  linkText?: string;
}

export interface CheckboxComponent extends WidgetComponent {
  innerColor?: string;
  presetStyle?: string;
  height?: string;
  width?: string;
}

export interface ButtonComponent extends WidgetComponent {
  height?: string;
  width?: string;
  iconUrl?: string;
  iconSize?: number;
  iconPosition?: string;
}

export interface DiscountCodeComponent extends WidgetComponent {
  height?: string;
  width?: string;
  couponSetName?: string;
}

export type InternationalPhoneComponent = WidgetComponent;

export type ShareComponent = WidgetComponent;

export type CodeEditorComponent = WidgetComponent;

export type ShareCouponCodeComponent = WidgetComponent;

export interface DataRepeaterComponent extends WidgetComponent {
  dataRepeaterSettings: DataRepeaterSettings;
  itemBackground?: BackgroundProperties;
  itemMobile?: MobileProperties;
  itemBorder?: ComponentBorder;
  itemRadius?: ComponentRadius;
  itemPadding?: FourSideStylingEntity;
  itemShadow?: ComponentShadow;
  itemMargin?: FourSideStylingEntity;
  itemGap?: string;
}

export interface IFrameComponent extends WidgetComponent {
  iFrameUrl?: string;
  overflow?: IFrameOverflow;
}

export enum IFrameOverflow {
  Hide = 'Hide',
  Scroll = 'Scroll',
}

export interface ComponentGroup extends WidgetComponent {
  height?: string;
  width?: string;
}

export interface SpacerComponent extends WidgetComponent {
  height: string;
}

export interface WidgetComponentWithHover extends WidgetComponent {
  backgroundColor?: string;
  fontColor?: string;
  border: ComponentBorder;
}

export interface WidgetComponentWithOptions extends WidgetComponent {
  size?: string;
  url?: string;
}

export interface DeadlineComponentProperties {
  deadlineDate?: Date;
  deadlineOccurrence?: DeadlineOccurrence;
  duration?: number;
  durationUnit?: DeadlineDurationUnit;
  format?: DeadlineFormat;
  delimiterText?: string;
  delimiterColor?: string;
  background?: BackgroundProperties;
  border?: ComponentBorder;
  radius?: ComponentRadius;
  padding?: FourSideStylingEntity;
  shadow?: ComponentShadow;
  margin?: FourSideStylingEntity;
}

export interface LabelProperties {
  showLabels?: boolean;
  labelPosition?: LabelPosition;
  textProperties?: TextProperties;
  background?: BackgroundProperties;
  border?: ComponentBorder;
  radius?: ComponentRadius;
  padding?: FourSideStylingEntity;
  shadow?: ComponentShadow;
  margin?: FourSideStylingEntity;
}

export interface ButtonProperties {
  iconPosition?: string;
  iconSize?: string;
  iconUrl?: string;
  animation?: string;
  animationDuration?: number;
  hover?: ComponentHover;
}

export enum DeadlineOccurrence {
  OnlyOnce = 'OnlyOnce',
  EveryDay = 'EveryDay',
}

export enum DeadlineDurationUnit {
  Minutes = 'Minutes',
  Hours = 'Hours',
  Days = 'Days',
}

export enum DeadlineFormat {
  MinutesSeconds = 'MinutesSeconds',
  HoursMinutesSeconds = 'HoursMinutesSeconds',
  DaysHoursMinutes = 'DaysHoursMinutes',
  DaysHoursMinutesSeconds = 'DaysHoursMinutesSeconds',
  Days = 'Days',
  Hours = 'Hours',
}

export enum LabelPosition {
  Above = 'Above',
  Below = 'Below',
  Right = 'Right',
  Left = 'Left',
  AboveBox = 'AboveBox',
  BelowBox = 'BelowBox',
}

export interface DeadlineLabels {
  days?: string;
  hours?: string;
  minutes?: string;
  seconds?: string;
}

export interface DeadlineComponent extends WidgetComponent {
  deadlineProperties: DeadlineComponentProperties;
  textProperties: TextProperties;
  labels?: DeadlineLabels;
  labelProperties: LabelProperties;
  background?: BackgroundProperties;
  border: ComponentBorder;
  radius: ComponentRadius;
  padding?: FourSideStylingEntity;
  shadow: ComponentShadow;
  margin?: FourSideStylingEntity;
  actionType?: ButtonActionType;
  actionValue?: string;
  actionScript?: string;
}

export enum IconPosition {
  Top = 'Top',
  Bottom = 'Bottom',
  Right = 'Right',
  Left = 'Left',
  TopRight = 'TopRight',
  TopLeft = 'TopLeft',
}

export enum ButtonListFormat {
  IconAndText = 'IconAndText',
  IconOnly = 'IconOnly',
  TextOnly = 'TextOnly',
}

export interface ButtonListProperties {
  itemWidth?: string;
  itemHeight?: string;
  format?: ButtonListFormat;
  iconSize?: number;
  spaceBetween?: number;
  spaceInside?: number;
  shadow?: ComponentShadow;
  radius?: ComponentRadius;
  choicesPerRow?: number;
  allowMultipleSelection?: boolean;
  iconPosition?: IconPosition;
  checkedIconUrl?: string;
  checkedBorder?: ComponentBorder;
  checkedBackgroundColor?: string;
  checkedFontColor?: string;
  uncheckedIconUrl?: string;
  uncheckedBorder?: ComponentBorder;
  uncheckedBackgroundColor?: string;
  uncheckedFontColor?: string;
  correctBackgroundColor?: string;
  wrongBackgroundColor?: string;
  correctBorder?: ComponentBorder;
  wrongBorder?: ComponentBorder;
  labelType?: string;
  labelTextMinimum?: string;
  labelTextMaximum?: string;
  selectEffect?: SelectEffect;
  mobile?: MobileButtonPropertiesSettings;
  iconColor?: string;
  fillPerRow?: number;
}

export enum Direction {
  Row = 'Row',
  Column = 'Column',
  RowReverse = 'RowReverse',
  ColumnReverse = 'ColumnReverse',
}

export enum ActionOnComponent {
  Add = 'Add',
  Remove = 'Remove',
  Edit = 'Edit',
}

export interface ActionComponent {
  action: ActionOnComponent;
  component: WidgetComponent | WidgetState;
}

export interface MobileButtonPropertiesSettings {
  size: string;
  gap: string;
  fontSize?: string;
  lineHeight?: string;
}

export interface ButtonListComponent extends WidgetComponent {
  buttonListProperties: ButtonListProperties;
  textProperties: TextProperties;
  background?: BackgroundProperties;
  border: ComponentBorder;
  radius: ComponentRadius;
  padding?: FourSideStylingEntity;
  shadow: ComponentShadow;
  margin?: FourSideStylingEntity;
}

export interface WidgetComponentPatchRequest {
  components: Array<WidgetComponentPatchRequestItem>;
}

export interface WidgetComponentPatchRequestItem {
  id?: number;
  data?: Array<PatchRequest>;
}

export interface PatchRequest {
  op?: string;
  path?: string;
  value?: string;
}

interface WcagProperties {
  description?: string;
  role?: string;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
}

export interface WidgetComponent {
  id: number;
  /** The saved content's version; a save of an older copy is refused with 409. */
  version?: string;
  type: WidgetComponentType;
  subType: WidgetComponentSubType;
  $type?: string;
  favourite?: boolean;
  hideOnMobile?: boolean;
  hideOnDesktop?: boolean;
  children?: Array<WidgetComponent>;
  listItems?: Array<ComponentListItem>;
  height?: string;
  width?: string;
  alignment?: {
    horizontal?: ComponentAlignment;
    vertical?: ComponentAlignment;
  };
  direction?: Direction;
  positionCorner?: PositionCorner;
  positionX?: number;
  positionY?: number;
  hover?: ComponentHover;
  required?: boolean;
  mobile?: MobileProperties;
  actionType?: ButtonActionType;
  actionValue?: string;
  actionScript?: string;
  actionShouldLogClick?: boolean;
  actionAutomationId?: string;
  /** Automation fired when the component is shown rather than interacted with, e.g. a coupon distributed. */
  impressionAutomationId?: string;
  actionGoalTriggerbeeLog?: string;
  actionOpenCampaignId?: number;
  actionTriggerDelay?: number;
  actionTagVisitor?: string;
  textProperties?: TextProperties;
  background?: BackgroundProperties;
  border?: ComponentBorder;
  radius?: ComponentRadius;
  padding?: FourSideStylingEntity;
  shadow?: ComponentShadow;
  margin?: FourSideStylingEntity;
  visualEffect?: ContainerVisualEffect;
  countrySelectProperties?: CountrySelectProperties;
  url?: string;
  iconUrl?: string;
  iconSize?: number;
  iconPosition?: string;
  iconColor?: string;
  animation?: string;
  animationDuration?: number;
  externalId?: string;
  dataRepeaterSettings?: any; // TODO: Typed
  // Only used in UI
  containerId?: number;
  order?: number;
  parentId?: number;
  parentComponentType?: WidgetComponentType;
  parentComponentSubType?: WidgetComponentSubType;
  parentBackground?: BackgroundProperties;
  grandparentId?: number;
  grandparentSubType?: WidgetComponentSubType;
  name?: string;
  position?: string;
  label?: TextProperties;
  couponCodeProperties?: CouponCodeProperties;
  referFriendProperties?: ReferFriendProperties;
  copyTextProperties?: CopyTextProperties;
  codeEditorProperties?: CodeEditorProperties;
  passwordProperties?: PasswordProperties;
  validationProperties?: ValidationProperties;
  spinTheWheelProperties?: SpinTheWheelProperties;
  memoryProperties?: MemoryProperties;
  subscriptionCheckboxProperties?: SubscriptionCheckboxProperties;
  wcagProperties?: WcagProperties;
  buttonListProperties?: ButtonListProperties;
  rotation?: number;
  flipAlignment?: FlipAlignment;
  stepNavigationProperties?: StepNavigationProperties;
  stepContainerProperties?: StepContainerProperties;
}

export interface MobileProperties {
  width?: string;
  height?: string;
  padding?: FourSideStylingEntity;
  margin?: FourSideStylingEntity;
  fontSize?: string;
  lineHeight?: string;
  letterSpacing?: string;
  direction?: Direction;
  alignment?: { vertical?: ComponentAlignment };
}

export interface ComponentListItem {
  isKeyInputFocused?: boolean;
  key?: string;
  value?: string;
  uncheckedIconUrl?: string;
  checkedIconUrl?: string;
  hasAdditionalAction?: boolean;
  isEditing?: boolean;
  hasError?: boolean;
  isPanelExpanded?: boolean;
  action?: ListItemAction;
  isCorrect?: boolean;
  score?: number;
  id?: number;
  defaultChecked?: string | null;
}

export interface ListItemAction {
  actionType?: string;
  actionValue?: string;
  actionGoalTriggerbeeLog?: string;
  actionTagVisitor?: string;
  actionOpenCampaignId?: string;
  actionScript?: string;
  actionShouldLogClick?: string;
}

export interface SubmitButtonAction {
  actionTagVisitor?: string;
  actionOpenCampaignId?: string;
  actionScript?: string;
}

export interface TextProperties {
  text: string;
  color?: string;
  font?: string;
  fontSize?: string;
  lineHeight?: string;
  letterSpacing?: string;
  fontWeight?: number;
  alignment?: TextAlignment;
  margin?: FourSideStylingEntity;
  overflow?: TextOverflow;
  textShadow?: ComponentShadow;
}

export interface BackgroundProperties {
  color?: string;
  size?: BackgroundImageSize;
  position?: BackgroundImagePosition;
  imageUrl?: string;
  gradient?: string;
}

export interface ComponentBorder extends FourSideStylingEntity {
  color?: string;
  style?: string;
  width?: string;
}

export interface ComponentRadius {
  roundValue?: string | number;
  topLeft?: string | number;
  topRight?: string | number;
  bottomLeft?: string | number;
  bottomRight?: string | number;
}

export interface ComponentShadow {
  horizontalLength?: string | number;
  verticalLength?: string | number;
  blur?: string | number;
  spread?: string | number;
  color?: string;
}

export interface ComponentHover {
  backgroundColor?: string;
  fontColor?: string;
  border?: ComponentBorder;
  animation?: string;
}

export interface ContainerVisualEffect {
  effect?: ContainerEffectType;
  direction?: RelativeDirection;
  animationDuration?: number;
  startPosition?: AnimationStartPosition;
  wrapText?: boolean;
  repeat?: boolean;
}

export enum ContainerEffectType {
  SlideIn = 'SlideIn',
  Marquee = 'Marquee',
}

export enum RelativeDirection {
  Left = 'Left',
  Right = 'Right',
  Up = 'Up',
  Down = 'Down',
}

export enum AnimationStartPosition {
  Top = 'Top',
  Bottom = 'Bottom',
  Left = 'Left',
  Right = 'Right',
  Center = 'Center',
}

export interface CountrySelectProperties {
  include?: string[];
  label?: CountryLabel;
  subLabels?: CountryLabel[];
  hideFlags?: boolean;
  useBrowserLanguage?: boolean;
  defaultCountry?: string;
}

export interface CouponCodeProperties {
  couponSetId?: string;
  fallback?: string;
  copyMessage?: string;
  scratch?: ScratchProperties;
}

export interface ScratchProperties {
  color?: string;
  imageUrl?: string;
  size?: BackgroundImageSize;
  position?: BackgroundImagePosition;
  text?: string;
  textColor?: string;
}

export interface ReferFriendProperties {
  path?: string;
  message?: string;
  queryString?: string;
}

export interface CodeEditorProperties {
  html?: string;
  javaScript?: string;
  css?: string;
}

export interface CopyTextProperties {
  path?: string;
  message?: string;
  copyMessage?: string;
}

export interface PasswordProperties {
  minimumLength?: number;
  maximumLength?: number;
  requireUppercaseAndLowercase?: boolean;
  requireSymbols?: boolean;
  requireNumbers?: boolean;
  toggleVisibility?: boolean;
  errorMessage?: string;
}

export interface ValidationProperties {
  type?: ValidationType;
  customRegexPattern?: string;
  errorMessage?: string;
  minAgeLimit?: number;
}

export interface SpinTheWheelProperties {
  wheelSize: number;
  wheelAutoStartSpin: boolean;
  wheelSpinDuration: number;
  wheelBorderColor: string;
  wheelBorderWidth: number;
  wheelShadowColor: string;
  wheelShadowWidth: number;
  wheelNailImage: string;
  wheelNailImageWidth: number;
  wheelInnerShadowWidth: number;
  wheelInnerShadowColor: string;

  spinButtonLabel: string;
  spinButtonFont: string;
  spinButtonFontSize: number;
  spinButtonTextColor: string;
  spinButtonBackgroundColor: string;
  spinButtonImage: string;
  spinButtonHideOnSpin: boolean;

  sliceItems: SpinTheWheelSliceItem[];
  sliceFont: string;
  sliceFontSize: number;
  sliceLabelRotation: string;

  prizeFont: string;
  prizeFontSize: number;
  prizeTextColor: string;
  prizeBackgroundColor: string;
  prizeButtonTextColor: string;
  prizeButtonBackgroundColor: string;
  prizeWonText: string;
  prizeCopyMessage: string;
  prizeAnimateConfetti: boolean;
  mobile?: {
    wheelNailImageWidth: number;
    spinButtonFontSize: number;
    sliceFontSize: number;
    prizeFontSize: number;
  };
}

export interface MemoryProperties {
  cardBackside: string;
  cards: MemoryCard[];
  cardCenterCard: MemoryCardCenterCard;
  resultFont: string;
  resultFontSize: number;
  resultTextColor: string;
  resultBackgroundColor: string;
  resultButtonTextColor: string;
  resultButtonBackgroundColor: string;
  resultWonText: string;
  resultWonButtonText: string;
  resultCopyMessage: string;
  resultAnimateConfetti: boolean;
  resultCouponCode: string;
  resultStateId: number;
  cardImageWidth: number;
  cardRowGap: number;
  cardColumnGap: number;
  mobile: {
    resultFontSize: number;
    cardRowGap: number;
    cardColumnGap: number;
  };
}

export enum StepNavigationButtonDesign {
  None = 'None',
  Text = 'Text',
  Icon = 'Icon',
  IconAndText = 'IconAndText',
}

export enum StepNavigationLastButtonType {
  Normal = 'Normal',
  Close = 'Close',
  Submit = 'Submit',
}

export enum StepNavigationProgressBar {
  None = 'None',
  Dots = 'Dots',
  Numbers = 'Numbers',
  NumbersSlashAll = 'NumbersSlashAll',
  Line = 'Line',
}

export interface StepNavigationGradient {
  startColor: string;
  endColor: string;
}

export interface StepNavigationProperties {
  previousButtonDesign: StepNavigationButtonDesign;
  nextButtonDesign: StepNavigationButtonDesign;
  lastButtonType: StepNavigationLastButtonType;
  progressBar: StepNavigationProgressBar;
  nextButtonText: string;
  previousButtonText: string;
  lastButtonText: string;
  gradient?: StepNavigationGradient;
}

export interface StepContainerProperties {
  primaryCanvasType: PrimaryCanvasType;
}

export enum PrimaryCanvasType {
  InnerSteps = 'InnerSteps',
  Questions = 'Questions',
}

export interface MemoryCard {
  image: string;
}

export interface MemoryCardCenterCard {
  image: string;
}

export interface SpinTheWheelSliceItem {
  label: string;
  backgroundColor: string;
  image: string;
  weight: number;
  probability?: number;
  isWinner: boolean;
  resultCouponCode?: string;
  resultCouponSetId?: string;
  resultCouponSetName?: string;
  resultCouponCodeFallback?: string;
  resultStateId?: number;
  isPanelExpanded?: boolean;
  resultSelected?: string;
}

export interface SubscriptionCheckboxProperties {
  sourceIntegration?: IntegrationType;
}

export enum ValidationType {
  Numerical = 'Numerical',
  SwedishIdNumber = 'SwedishIdNumber',
  SwedishIdNumberTenDigits = 'SwedishIdNumberTenDigits',
  FinnishIdNumber = 'FinnishIdNumber',
  SwedishZipCode = 'SwedishZipCode',
  CustomRegex = 'CustomRegex',
  MinAge = 'MinAge',
}

export enum CountryLabel {
  Name = 'Name',
  Iso = 'Iso',
  Native = 'Native',
  DialCode = 'DialCode',
}

export enum CountrySelectFilter {
  All = 'All',
  Sovereign = 'Sovereign',
  Eu = 'Eu',
  Custom = 'Custom',
}

export interface Country {
  name: string;
  native: string;
  iso: string;
  dialCode: string;
  sovereign: boolean;
  eu: boolean;
}

export interface WidgetTransition {
  entryAnimation?: EntryAnimationType;
  stepAnimation?: StepAnimationType;
  exitAnimation?: ExitAnimationType;
  animationDuration?: number;
  isElastic?: boolean;
  morphBetweenStates?: boolean;
}

export interface FourSideStylingEntity {
  roundValue?: string | number;
  top?: string | number;
  right?: string | number;
  bottom?: string | number;
  left?: string | number;
}

export class FourSideStylingEntityWrapper {
  constructor(public entity: FourSideStylingEntity) {
    this.setDefaultValues();
  }

  private setDefaultValues() {
    if (!this.entity) {
      this.entity = {};
    }
    if (this.shouldSet(this.entity.top)) {
      this.setTop('0px');
    }
    if (this.shouldSet(this.entity.bottom)) {
      this.setBottom('0px');
    }
    if (this.shouldSet(this.entity.left)) {
      this.setLeft('0px');
    }
    if (this.shouldSet(this.entity.right)) {
      this.setRight('0px');
    }
  }

  private shouldSet(value: string | number) {
    if (value) {
      return false;
    }
    return true;
  }

  public getEntity(): FourSideStylingEntity {
    return this.entity;
  }

  public setTop(value: string | number): FourSideStylingEntityWrapper {
    this.entity.top = value;
    return this;
  }

  public setBottom(value: string | number): FourSideStylingEntityWrapper {
    this.entity.bottom = value;
    return this;
  }
  public setLeft(value: string | number): FourSideStylingEntityWrapper {
    this.entity.left = value;
    return this;
  }
  public setRight(value: string | number): FourSideStylingEntityWrapper {
    this.entity.right = value;
    return this;
  }
}

/* Helper classes only for front-end */
export interface EditModeComponent {
  component: WidgetComponent;
  originalComponent: WidgetComponent;
  latestUpdatedPart?: EditedComponentPart;
  sidebarSection?: EditedComponentSidebarSection;
  isCloseEditorComponent?: boolean;
}

export interface CustomProfileProperty {
  id?: number;
  mapFrom?: string;
  mapTo?: string;
  name?: string;
}

export interface EmbeddedPlaceholder {
  id: number;
  name: string;
  selector: string;
  embedMethod: EmbedMethod;
  previewUrl: string;
  widthInEditor: string;
  heightInEditor: string;
  inUse: boolean;
}

export enum EntryAnimationType {
  ToneIn = 'ToneIn',
  SlideInLeft = 'SlideInLeft',
  SlideInRight = 'SlideInRight',
  SlideInTop = 'SlideInTop',
  SlideInBottom = 'SlideInBottom',
  WiggleIn = 'WiggleIn',
  Reset = 'Reset',
}

export enum StepAnimationType {
  ToneIn = 'ToneIn',
  SlideInLeft = 'SlideInLeft',
  SlideInRight = 'SlideInRight',
  SlideInTop = 'SlideInTop',
  SlideInBottom = 'SlideInBottom',
}

export enum ExitAnimationType {
  ToneOut = 'ToneOut',
  SlideOutLeft = 'SlideOutLeft',
  SlideOutRight = 'SlideOutRight',
  SlideOutTop = 'SlideOutTop',
  SlideOutBottom = 'SlideOutBottom',
  MorphNextState = 'MorphNextState',
}

export enum FlipAlignment {
  Vertical = 'Vertical',
  Horizontal = 'Horizontal',
}

export enum EmbedMethod {
  Replace = 'Replace',
  AppendChild = 'AppendChild',
}

export enum EditedComponentPart {
  Text = 'Text',
  Style = 'Style',
  ItemStyle = 'ItemStyle',
  Preset = 'Preset',
  TextReplace = 'TextReplace',
  Formatter = 'Formatter',
}

export enum EditedComponentSidebarSection {
  Advanced = 'Advanced',
  Appearance = 'Appearance',
  ItemAppearance = 'ItemAppearance',
  ButtonActions = 'ButtonActions',
  Checkbox = 'Checkbox',
  Deadline = 'Deadline',
  Dimensions = 'Dimensions',
  Hover = 'Hover',
  InputField = 'InputField',
  ItemList = 'ItemList',
  Label = 'Label',
  Presets = 'Presets',
  IFrame = 'IFrame',
  RadioButtons = 'RadioButtons',
  Text = 'Text',
  InputLabel = 'InputLabel',
  Transition = 'Transition',
  Rating = 'Rating',
  Animation = 'Animation',
  Coupon = 'Coupon',
  ReferFriend = 'ReferFriend',
  TextCopy = 'TextCopy',
  CodeEditorHtml = 'CodeEditorHtml',
  AddAfterCopy = 'AddAfterCopy',
  SpinTheWheelSettings = 'SpinTheWheelSettings',
  SpinTheWheelButton = 'SpinTheWheelButton',
  SpinTheWheelSlices = 'SpinTheWheelSlices',
  SpinTheWheelAppearance = 'SpinTheWheelAppearance',
  SpinTheWheelPrize = 'SpinTheWheelPrize',
  MemoryPrize = 'MemoryPrize',
  MemoryCards = 'MemoryCards',
  DataSource = 'DataSource',
  Wcag = 'Wcag',
  ProductTour = 'ProductTour',
  DataRepeater = 'DataRepeater',
  StepNavigation = 'StepNavigation',
  StepContainer = 'StepContainer',
  AnswerFormatting = 'AnswerFormatting',
}

export enum ButtonActionType {
  None = 'None',
  OpenUrl = 'OpenUrl',
  OpenUrlNewTab = 'OpenUrlNewTab',
  SwitchState = 'SwitchState',
  CloseWidget = 'CloseWidget',
  SwitchStateNext = 'SwitchStateNext',
  SwitchStatePrevious = 'SwitchStatePrevious',
  SubmitForm = 'SubmitForm',
  OpenUrlNewTabAndSwitchState = 'OpenUrlNewTabAndSwitchState',
  CopyToClipboard = 'CopyToClipboard',
  MinimizeWidget = 'MinimizeWidget',
  PhoneShareMenu = 'PhoneShareMenu',
  OpenNativeApp = 'OpenNativeApp',
}

export class ComponentDescription {
  public isDisabled?: boolean;
  public disabledReason?: string;
  public isDropDisabled?: boolean;
  public position?: number; // position in the add view (0 - n), used for blink animation
  public clipboardComp?: any;
  public imageData?: MediaModel;

  constructor(
    public title: string,
    public icon: string,
    public type: WidgetComponentType,
    public subType: WidgetComponentSubType,
    public available: boolean = true,
    public isAdminOnly: boolean = false,
    public isNew: boolean = false,
    public clipId?: number,
    public requiresPrimaryCanvas?: boolean,
    public requiresDataRepeater?: boolean,
  ) {}
}

export class ComponentDescriptionGroup {
  constructor(
    public title: string,
    public type: ComponentDescriptionGroupType,
    public components: Array<ComponentDescription>,
  ) {}
}

export enum ComponentDescriptionGroupType {
  Layout = 'Layout',
  Static = 'Static',
  Form = 'Form',
  Triggerbee = 'Triggerbee',
  Surveys = 'Surveys',
  ThirdPartyData = 'ThirdPartyData',
  Clipboard = 'Clipboard',
}

export interface WidgetFontDescription {
  name?: string;
  displayName?: string;
  cssLink?: string;
  isDefault?: boolean;
  type?: AccountCustomFontType;
  fontFiles?: Array<AccountCustomFontFile>;
}

export enum SidebarState {
  Layout = 'Layout',
  New = 'New',
  Library = 'Library',
  Component = 'Component',
  PoweredBy = 'PoweredBy',
  Settings = 'Settings',
  General = 'General',
  Style = 'Style',
  CustomScripts = 'CustomScripts',
  PrimaryCanvasComponent = 'PrimaryCanvasComponent',
}

export enum InputTextType {
  Text = 'Text',
  Email = 'Email',
  Number = 'Number',
}

export enum WidgetComponentType {
  // Types for state
  Regular = 'Regular',
  Teaser = 'Teaser',
  Success = 'Success',
  // Types for component
  ContainerFloating = 'Floating',
  ContainerRegular = 'Regular',
  Image = 'Image',
  Text = 'Text',
  /*FloatingImage = 'FloatingImage',*/
  Spacer = 'Spacer',
  /*CloseButton = 'CloseButton',*/
  InputText = 'InputText',
  Button = 'Button',
  ButtonList = 'ButtonList',
  Checkbox = 'Checkbox',
  Deadline = 'Deadline',
  DiscountCode = 'DiscountCode',
  Group = 'Group',
  BulletList = 'BulletList',
  Date = 'Date',
  Dropdown = 'Dropdown',
  Placeholder = 'Placeholder',
  // Removed: ProductRecommendation = 'ProductRecommendation',
  IFrame = 'IFrame',
  InternationalPhone = 'InternationalPhone',
  Share = 'Share',
  CodeEditor = 'CodeEditor',
  ShareCampaign = 'ShareCampaign',
  SpinTheWheel = 'SpinTheWheel',
  Memory = 'Memory',
  DataRepeater = 'DataRepeater',
  StepContainer = 'StepContainer',
  StepNavigation = 'StepNavigation',
}

export enum WidgetComponentSubType {
  InputTextEmail = 'InputTextEmail',
  InputTextName = 'InputTextName',
  InputTextFirstName = 'InputTextFirstName',
  InputTextLastName = 'InputTextLastName',
  InputTextPhone = 'InputTextPhone',
  InputTextRegular = 'InputTextRegular',
  InputTextPassword = 'InputTextPassword',
  InputTextArea = 'InputTextArea',
  ImageRegular = 'ImageRegular',
  ImageFloating = 'ImageFloating',
  ButtonRegular = 'ButtonRegular',
  ButtonSubmit = 'ButtonSubmit',
  ButtonFloating = 'ButtonFloating',
  ButtonInstagramFollow = 'ButtonInstagramFollow',
  ButtonHide = 'ButtonHide',
  TextRegular = 'TextRegular',
  TextFloating = 'TextFloating',
  SpacerRegular = 'SpacerRegular',
  ContainerRegular = 'ContainerRegular',
  DeadlineRegular = 'DeadlineRegular',
  DeadlineCountdown = 'DeadlineCountdown',
  CheckboxConsent = 'CheckboxConsent',
  CheckboxRegular = 'CheckboxRegular',
  DiscountCodeRegular = 'DiscountCodeRegular',
  ShareCouponCode = 'ShareCouponCode',
  GroupRegular = 'GroupRegular',
  GroupTwoColumns = 'GroupTwoColumns',
  GroupThreeColumns = 'GroupThreeColumns',
  GroupFourColumns = 'GroupFourColumns',
  GroupFiveColumns = 'GroupFiveColumns',
  GroupFirstAndLastName = 'GroupFirstAndLastName',
  GroupEmailAndSubmit = 'GroupEmailAndSubmit',
  GroupTwoButtons = 'GroupTwoButtons',
  BulletListRegular = 'BulletListRegular',
  ButtonListRadio = 'ButtonListRadio',
  ButtonListChoices = 'ButtonListChoices',
  ButtonListCheckbox = 'ButtonListCheckbox',
  DateRegular = 'DateRegular',
  DropdownRegular = 'DropdownRegular',
  DropdownCountry = 'DropdownCountry',
  PlaceholderRegular = 'PlaceholderRegular',
  // Removed: ProductRecommendationRegular = 'ProductRecommendationRegular',
  IFrameRegular = 'IFrameRegular',
  ButtonListStarRating = 'ButtonListStarRating',
  ButtonListNumericalRating = 'ButtonListNumericalRating',
  ButtonListNpsRating = 'ButtonListNpsRating',
  ButtonListSmileyRating = 'ButtonListSmileyRating',
  ButtonListThumbRating = 'ButtonListThumbRating',
  CsatPlaceholder = 'CsatPlaceholder',
  ButtonListCsatStarRating = 'ButtonListCsatStarRating',
  ButtonListCsatSmileyRating = 'ButtonListCsatSmileyRating',
  ButtonListCsatNumericalRating = 'ButtonListCsatNumericalRating',
  ButtonListSubscriptionCheckbox = 'ButtonListSubscriptionCheckbox',
  InternationalPhoneRegular = 'InternationalPhoneRegular',
  ButtonShare = 'ButtonShare',
  ShareCampaignGroup = 'ShareCampaignGroup',
  TextCopy = 'TextCopy',
  CodeEditorHtml = 'CodeEditorHtml',
  ClipboardPlaceHolder = 'ClipboardPlaceHolder',
  SpinTheWheel = 'SpinTheWheel',
  Memory = 'Memory',
  DataRepeaterRegular = 'DataRepeaterRegular',
  DataRepeaterLeaderboard = 'DataRepeaterLeaderboard',
  StepContainerRegular = 'StepContainerRegular',
  StepNavigationRegular = 'StepNavigationRegular',
}

export enum WidgetLayout {
  Popup = 'Popup',
  Callout = 'Callout',
  Embedded = 'Embedded',
  Sidebar = 'Sidebar',
  Panel = 'Panel',
  Fullscreen = 'Fullscreen',
  ProductTour = 'ProductTour',
  Edges = 'Edges',
}

export enum WidgetPosition {
  Center = 'Center',
  Top = 'Top',
  Bottom = 'Bottom',
  Left = 'Left',
  Right = 'Right',
  TopLeft = 'TopLeft',
  TopRight = 'TopRight',
  BottomLeft = 'BottomLeft',
  BottomRight = 'BottomRight',
  Embedded = 'Embedded',
  ProductTour = 'ProductTour',
  Fullscreen = 'Fullscreen',
}

export enum TagColor {
  Mojo = '#CA6140',
  FountainBlue = '#4CB5AB',
  BondiBlue = '#0093B2',
  HalfBaked = '#92CBC5',
  WildStrawberry = '#FF4081',
  Bittersweet = '#FF6E6E',
  CannonPink = '#8A4B76',
  LilacBush = '#AB7FD1',
  SmaltBlue = '#508989',
  Russett = '#845A5A',
  WildWillow = '#C7CB76',
  Tacha = '#D5B05D',
  BlueViolet = '#765EB9',
  ShipCove = '#6898C1',
  Downy = '#5BCEAB',
  Tapestry = '#B55899',
}

export enum WidgetDeviceView {
  Mobile = 'Mobile',
  Desktop = 'Desktop',
}

export enum BackgroundImageSize {
  Cover = 'cover',
  Contain = 'contain',
  Fit = 'fit',
}

export enum TextAlignment {
  Left = 'Left',
  Right = 'Right',
  Center = 'Center',
  Justify = 'Justify',
}

export enum TextOverflow {
  FreeLine = 'FreeLine',
  OneLine = 'OneLine',
  TwoLines = 'TwoLines',
  ThreeLines = 'ThreeLines',
}

export enum EditingDevice {
  Mobile = 'Mobile',
  Any = 'Any',
}

export enum ComponentAlignment {
  Left = 'Left',
  Right = 'Right',
  Center = 'Center',
  Top = 'Top',
  Bottom = 'Bottom',
  SpaceBetween = 'SpaceBetween',
  SpaceAround = 'SpaceAround',
  SpaceEvenly = 'SpaceEvenly',
}

export enum PositionCorner {
  TopLeft = 'TopLeft',
  BottomLeft = 'BottomLeft',
  TopRight = 'TopRight',
  BottomRight = 'BottomRight',
  BottomCenter = 'BottomCenter',
  TopCenter = 'TopCenter',
}

export enum BackgroundImagePosition {
  LeftTop = 'left top',
  LeftCenter = 'left center',
  LeftBottom = 'left bottom',
  RightTop = 'right top',
  RightCenter = 'right center',
  RightBottom = 'right bottom',
  CenterTop = 'center top',
  CenterCenter = 'center center',
  CenterBottom = 'center bottom',
}

// TODO: Properties should be renamed in later versions of the api to correspond the ones on Campaign Statistics
export interface WidgetStatistics {
  id?: number;
  open?: number;
  dismissal?: number;
  conversion?: number;
  clickThrough?: number;
  couponsCopied?: number;
  couponsCopiedRate?: number;
  surveyResponses?: number;
  conversionRate?: number;
  ctr?: number;
  surveyResponsesPartial?: number;
  conversionPartial?: number;
  share?: number;
}

export interface WidgetStatisticsResponse {
  error: string;
  data: Array<WidgetStatistics>;
}

export interface WidgetDailyStatistics {
  count?: number;
  date?: string;
}

export interface WidgetDailyStatisticsWrapper {
  name?: string;
  count?: number;
  items?: Array<any>;
}

export interface WidgetDailyStatisticsResponse {
  error?: string;
  data?: WidgetDailyStatisticsWrapper;
}

export enum WidgetUpdateProgress {
  UpToDate = 'UpToDate',
  Updating = 'Updating',
  OutDate = 'OutDate',
  Error = 'Error',
}

export interface LayoutsPositions {
  popup: Array<WidgetLayoutSettings>;
  callout: Array<WidgetLayoutSettings>;
  panel: Array<WidgetLayoutSettings>;
  // Embedded: ItemLayout[];features in a progress
}

export interface LayoutsPositionGroup {
  type: string;
  layouts: Array<WidgetLayoutSettings>;
}

export interface WidgetLayoutSettings {
  nameVar: string;
  imgPath: string;
  imgPathSelected: string;
  imgPathDark: string;
  imgPathSelectedDark: string;
  position: WidgetPosition;
  layout: WidgetLayout;
  choose: boolean;
  isBeta?: boolean;
}

export interface TagItemList {
  id?: number;
  name: string;
  organizations: string[] | null;
  type: string | number;
}

export interface TagFlyoutResponse {
  tag?: TagItemList;
  isRemoved?: boolean;
  isCreated?: boolean;
  widgetId?: number;
}

export interface EditResponseListFlyoutResponse {
  id: string;
  name: string;
  columns: Column[];
}

export interface FilterCampaignFlyoutResponse {
  campaignFilterRequest: CampaignFilterRequest;
  filterId?: number;
}

export interface UpgradeAccountFlyoutResponse {
  eventsPerMonth: number;
  accounts?: number;
  surveyResponsesPerMonth?: number;
  currency?: Currency;
  billingFrequency?: SubscriptionFrequency;
  price?: number;
  tier?: Tier;
}

export interface FunnelGraphModel {
  widgetId?: number;
  name?: string;
  data?: FunnelGraphItem[];
}

export interface FunnelGraphItem {
  stateId?: number;
  title?: string;
  text?: string;
  value?: number;
  dropData?: number;
  percent?: number;
  gainData?: number;
  /** Share of the funnel's first step this step reached, in percent (the "conversion from the top"
   * figure shown above the bars); undefined when the step has no data. */
  conversionPercent?: number;
  order?: number;
  type?: string;
}

export enum ConditionsLabel {
  alwaysShow = 'AlwaysShow',
  showLeftAndRightOnly = 'ShowLeftAndRightOnly',
}

export enum SelectEffect {
  blink = 'Blink',
  fancyBlink = 'FancyBlink',
}

export enum NameChart {
  grid = 'grid-chart',
  formResponses = 'form-responses-chart',
  referralConversion = 'referral-conversion-chart',
  gridReferralConversion = 'grid-referral-conversion-chart',
  gridShare = 'share-chat',
  gridRevenue = 'revenue-chart',
  revenue = 'referral-revenue-chart',
  gridViewReferral = 'grid-view-referral',
  trafficPerformance = 'traffic-performance-chart',
  campaignsPerformance = 'campaigns-performance-chart',
}

export enum AlternativeStepIntegration {
  MailChimp = 'MailChimp',
  RuleMailer = 'RuleMailer',
  Rule = 'Rule', // Needed for getting "lists" to work
  Voyado = 'Voyado',
  Emarsys = 'Emarsys',
  Custobar = 'Custobar',
  MailerLite = 'MailerLite',
  Centra = 'Centra',
  Shopify = 'Shopify',
  Shopware = 'Shopware',
  Klaviyo = 'Klaviyo',
}

export enum SidebarItemsType {
  DataSource = 'dataSource',
  Presets = 'presets',
  Deadline = 'deadline',
  Iframe = 'iFrame',
  RadioButtons = 'radioButtons',
  ItemList = 'itemList',
  Country = 'country',
  Coupon = 'coupon',
  CodeEditorHtml = 'codeEditorHtml',
  TextCopy = 'textCopy',
  ReferFriend = 'referFriend',
  Rating = 'rating',
  Dimensions = 'dimensions',
  Text = 'text',
  TextWithLabel = 'textWithLabel',
  Appearance = 'appearance',
  Animation = 'animation',
  ItemAppearance = 'item-appearance',
  Password = 'password',
  Labels = 'labels',
  Hover = 'hover',
  ButtonActions = 'buttonActions',
  Transition = 'transition',
  Advanced = 'advanced',
  InputField = 'inputField',
  Checkbox = 'checkbox',
  SpinTheWheelSettings = 'spinTheWheelSettings',
  SpinTheWheelSpinButton = 'spinTheWheelSpinButton',
  SpinTheWheelItems = 'spinTheWheelItems',
  SpinTheWheelPrize = 'spinTheWheelPrize',
  SpinTheWheelItemsAppearance = 'spinTheWheelItemsAppearance',
  MemoryPrize = 'memoryPrize',
  MemoryCards = 'memoryCards',
  Wcag = 'wcag',
  DeadlineSetup = 'deadlineSetup',
  DataRepeater = 'dataRepeater',
  StepContainer = 'stepContainer',
  StepNavigation = 'stepNavigation',
  Sticker = 'sticker',
  CloseButton = 'closeButton',
  AnswerFormatting = 'answerFormatting',
  Button = 'button',
}

export interface StatesInUse {
  alternativeState: LinkedState;
  submitBtnState: LinkedState;
  regularBtnState: LinkedState[];
}

export interface LinkedState {
  linkedState: number;
  sourceText: string;
  sourceStateOrder: number;
  sourceComponentId?: number;
  sourceStateId: number;
  sourceItemId?: number;
}

export class SplittedStates {
  constructor(
    public regulars?: Array<WidgetState>,
    public success?: WidgetState,
    public teaser?: WidgetState,
  ) {}

  public getChoosableStates(stateIdToExclude: number, languageService: LanguageService): Array<WidgetState> {
    const resultArray = [] as Array<WidgetState>;
    if (this.regulars && this.regulars.length > 0) {
      resultArray.push.apply(resultArray, this.regulars);
    }
    if (this.success) {
      resultArray.push(this.success);
    }
    if (this.teaser) {
      resultArray.push(this.teaser);
    }
    resultArray.forEach((state: WidgetState) => (state.name = this.getStateDisplayName(state.id, languageService)));
    return resultArray;
  }

  public getChoosableStatesWithExclude(stateIdToExclude: number, languageService: LanguageService): Array<WidgetState> {
    const resultArray = [] as Array<WidgetState>;
    if (this.regulars && this.regulars.filter((state) => state.id !== stateIdToExclude).length > 0) {
      resultArray.push.apply(
        resultArray,
        this.regulars.filter((state) => state.id !== stateIdToExclude),
      );
    }
    if (this.success && this.success.id !== stateIdToExclude) {
      resultArray.push(this.success);
    }
    if (this.teaser && this.teaser.id !== stateIdToExclude) {
      resultArray.push(this.teaser);
    }
    resultArray.forEach((state: WidgetState) => (state.name = this.getStateDisplayName(state.id, languageService)));
    return resultArray;
  }

  public getStateDisplayName(stateId: number, languageService: LanguageService): string {
    if (this.regulars) {
      const indexInRegulars = this.regulars.findIndex((state) => state.id === stateId);
      if (indexInRegulars !== -1) {
        return (
          languageService.translateInstantly('campaign.widget.state.regular') +
          (this.regulars.length > 1 ? ' ' + (indexInRegulars + 1) : '')
        );
      }
    }
    if (this.success && this.success.id === stateId) {
      return languageService.translateInstantly('campaign.widget.state.success');
    }
    if (this.teaser && this.teaser.id === stateId) {
      return languageService.translateInstantly('campaign.widget.state.minimized');
    }
    throw new Error('No state with this id found: ' + stateId);
  }
}

export interface CarouselSettings {
  itemsPerView: number;
}

export interface DataRepeaterSettings {
  dataSourceId?: string;
  dataSourceType?: DataSourceType;
  integration?: IntegrationType;
  maxItems?: number;
  sortBy?: string;
  descending?: boolean;
  filterProperty?: string;
  filterValues?: Array<string>;
  itemGap?: string;
  flexDirection?: Direction;
  carouselSettings?: CarouselSettings;
}

export interface AccountFileResponse {
  uri: string;
  lastModified?: Date;
}

export enum WidgetStateGroup {
  RegularStates = 'regular-states',
  InnerStates = 'inner-states',
  OutcomeStates = 'outcome-states',
}

export interface WidgetPreviewResponse {
  token?: string;
}
